// CalendarView.js - Enhanced Admin Interface with Token-Based Calendar Subscription
import React, { useState, useEffect, useContext } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Download, Users, Clock, MapPin, User, ChevronDown, Filter, Copy, CheckCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { authFetch } from './authFetch';
import { UserContext } from './App';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CalendarView() {
  const { currentUser } = useContext(UserContext);
  const isAdmin = currentUser?.groups?.some(g => g.name === 'Admin');
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [calendarType, setCalendarType] = useState(isAdmin ? 'admin' : 'agent'); // agent or admin
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agentStats, setAgentStats] = useState([]);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  
  // New state for calendar subscription
  const [subscriptionUrls, setSubscriptionUrls] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);

  // Show status messages
  const showStatus = (text, type = 'success') => {
    setStatusMessage({ text, type });
    setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
  };

  // Copy to clipboard function
  const copyToClipboard = async (url, urlType) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(urlType);
      setTimeout(() => setCopiedUrl(null), 2000);
      showStatus('Calendar URL copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedUrl(urlType);
        setTimeout(() => setCopiedUrl(null), 2000);
        showStatus('Calendar URL copied to clipboard!', 'success');
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        showStatus('Failed to copy URL to clipboard', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  // Open calendar app
  const openCalendarApp = (url, appType) => {
    let finalUrl = url;
    
    // Convert to webcal:// for better calendar app support
    if (appType === 'apple' || appType === 'outlook') {
      finalUrl = url.replace('https://', 'webcal://');
    }
    
    window.open(finalUrl, '_blank');
  };

  // Fetch calendar subscription URLs
  const fetchSubscriptionUrls = async () => {
    if (calendarType !== 'agent') return; // Only for agent view
    
    setSubscriptionLoading(true);
    try {
      const response = await authFetch(`${BASE_URL}/api/calendar/subscription-urls/`);
      
      if (response.ok) {
        const data = await response.json();
        setSubscriptionUrls(data);
      } else {
        console.error('Failed to fetch subscription URLs:', response.status);
        showStatus('Failed to load calendar subscription URLs', 'error');
      }
    } catch (error) {
      console.error('Error fetching subscription URLs:', error);
      showStatus('Error loading calendar subscription', 'error');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Fetch all agents for dropdown
  const fetchAllAgents = async () => {
    try {
      const response = await authFetch(`${BASE_URL}/api/auth/debug-groups/`);
      if (response.ok) {
        const data = await response.json();
        // Extract agents from the response or use a dedicated endpoint
        // For now, we'll use the agent stats to populate the dropdown
        const agents = agentStats.map(stat => ({
          id: stat.agent_id,
          username: stat.username,
          name: stat.agent_name
        }));
        setAllAgents(agents);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Fetch calendar data
  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const startDate = startOfMonth(currentDate);
      const endDate = endOfMonth(currentDate);
      
      const endpoint = calendarType === 'admin' ? '/api/calendar/admin/' : '/api/calendar/agent/';
      const params = new URLSearchParams({
        start: startDate.toISOString(),
        end: endDate.toISOString()
      });
      
      if (calendarType === 'admin' && selectedAgent) {
        params.append('agent', selectedAgent);
      }
      
      const response = await authFetch(`${BASE_URL}${endpoint}?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments);
        if (data.agent_stats) {
          setAgentStats(data.agent_stats);
        }
      } else {
        showStatus('Failed to fetch calendar data', 'error');
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      showStatus('Error loading calendar data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate, calendarType, selectedAgent]);

  useEffect(() => {
    if (calendarType === 'admin' && agentStats.length > 0) {
      fetchAllAgents();
    }
  }, [agentStats, calendarType]);

  // Fetch subscription URLs when switching to agent view
  useEffect(() => {
    if (calendarType === 'agent') {
      fetchSubscriptionUrls();
    }
  }, [calendarType]);

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Export functions
  const exportCalendar = async () => {
    try {
      const endpoint = calendarType === 'admin' ? '/api/calendar/export/admin.ics' : '/api/calendar/export/agent.ics';
      const params = new URLSearchParams();
      
      if (calendarType === 'admin' && selectedAgent) {
        params.append('agent', selectedAgent);
      }
      
      const url = `${BASE_URL}${endpoint}?${params}`;
      
      const response = await authFetch(url);
      if (response.ok) {
        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectURL;
        link.download = `calendar_${calendarType}_${selectedAgent || 'all'}_${format(new Date(), 'yyyy-MM-dd')}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectURL);
        showStatus('Calendar exported successfully!', 'success');
      } else {
        showStatus('Failed to export calendar', 'error');
      }
    } catch (error) {
      console.error('Error exporting calendar:', error);
      showStatus('Error exporting calendar', 'error');
    }
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  };

  // Get appointments for a specific day
  const getAppointmentsForDay = (day) => {
    return appointments.filter(appointment => 
      isSameDay(parseISO(appointment.date), day)
    );
  };

  // Render calendar day
  const renderCalendarDay = (day) => {
    const dayAppointments = getAppointmentsForDay(day);
    const isCurrentMonth = isSameMonth(day, currentDate);
    const isToday = isSameDay(day, new Date());

    return (
      <div
        key={day.toString()}
        className={`min-h-[120px] border border-gray-200 p-2 ${
          isCurrentMonth ? 'bg-white' : 'bg-gray-50'
        } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
      >
        <div className={`text-sm font-medium mb-1 ${
          isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
        } ${isToday ? 'text-blue-600' : ''}`}>
          {format(day, 'd')}
        </div>
        
        <div className="space-y-1">
          {dayAppointments.slice(0, 3).map((appointment, index) => (
            <div
              key={appointment.id}
              className="text-xs p-1 rounded bg-blue-100 text-blue-800 truncate cursor-pointer hover:bg-blue-200"
              title={`${appointment.title} - ${appointment.customer ? 
                `${appointment.customer_name || 'Customer'}` : 'No customer'
              }${calendarType === 'admin' && appointment.agent_name ? ` (${appointment.agent_name})` : ''}`}
            >
              <div className="font-medium truncate">{appointment.title}</div>
              <div className="text-blue-600 truncate">
                {format(parseISO(appointment.date), 'h:mm a')}
                {calendarType === 'admin' && appointment.agent_name && (
                  <span className="ml-1 text-xs">- {appointment.agent_name.split(' ')[0]}</span>
                )}
              </div>
            </div>
          ))}
          
          {dayAppointments.length > 3 && (
            <div className="text-xs text-gray-500 italic">
              +{dayAppointments.length - 3} more
            </div>
          )}
        </div>
      </div>
    );
  };

  // Enhanced Calendar Subscription Component
  const CalendarSubscriptionPanel = () => {
    if (calendarType !== 'agent' || !subscriptionUrls) return null;

    const { subscription_urls, instructions, notes } = subscriptionUrls;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" />
            <h4 className="text-sm font-medium text-blue-900">
              Calendar Subscription
            </h4>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setShowSubscriptionDetails(!showSubscriptionDetails)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              {showSubscriptionDetails ? 'Hide Details' : 'Show Setup'}
              <ChevronDown className={`h-4 w-4 ml-1 transform transition-transform ${showSubscriptionDetails ? 'rotate-180' : ''}`} />
            </button>
            
            <button
              onClick={fetchSubscriptionUrls}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              disabled={subscriptionLoading}
            >
              <RefreshCw className={`h-4 w-4 ${subscriptionLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <p className="text-sm text-blue-700 mb-3">
          Subscribe to automatically sync your appointments with your preferred calendar app.
        </p>

        {showSubscriptionDetails && (
          <div className="space-y-4">
            {Object.entries(subscription_urls).map(([key, url]) => (
              <div key={key} className="border border-blue-300 rounded-lg p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-gray-900 capitalize">
                    {key.replace('_', ' ')} URL
                  </h5>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Auto-updates hourly
                  </span>
                </div>
                
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="text"
                    value={url}
                    readOnly
                    className="flex-1 text-xs font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1 text-gray-700"
                  />
                  <button
                    onClick={() => copyToClipboard(url, key)}
                    className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                      copiedUrl === key
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {copiedUrl === key ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => openCalendarApp(url, 'apple')}
                    className="flex-1 bg-gray-900 text-white px-2 py-1 rounded text-xs font-medium hover:bg-gray-800 transition-colors flex items-center justify-center"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Apple Calendar
                  </button>
                  <button
                    onClick={() => openCalendarApp(url, 'google')}
                    className="flex-1 bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Google Calendar
                  </button>
                  <button
                    onClick={() => openCalendarApp(url, 'outlook')}
                    className="flex-1 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium hover:bg-blue-600 transition-colors flex items-center justify-center"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Outlook
                  </button>
                </div>
              </div>
            ))}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h5 className="font-medium text-amber-900 mb-2">Setup Instructions</h5>
              <ul className="text-xs text-amber-800 space-y-1">
                {Object.entries(instructions).map(([app, instruction]) => (
                  <li key={app}>
                    <strong className="capitalize">{app.replace('_', ' ')}:</strong> {instruction}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h5 className="font-medium text-gray-900 mb-2">Important Notes</h5>
              <ul className="text-xs text-gray-700 space-y-1">
                {notes.map((note, index) => (
                  <li key={index}>• {note}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Subscription Loading Component
  const CalendarSubscriptionLoading = () => {
    if (calendarType !== 'agent') return null;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center">
          <RefreshCw className="h-5 w-5 text-blue-500 mr-2 animate-spin" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">
              Calendar Subscription
            </h4>
            <p className="text-sm text-blue-700">
              Loading calendar subscription URLs...
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Agent Stats Panel
  const AgentStatsPanel = () => (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Users className="h-5 w-5 mr-2" />
        Agent Overview
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {agentStats.map(agent => (
          <div 
            key={agent.agent_id} 
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedAgent === agent.username 
                ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' 
                : 'hover:bg-gray-50 hover:border-gray-300'
            }`}
            onClick={() => setSelectedAgent(selectedAgent === agent.username ? '' : agent.username)}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900 truncate">{agent.agent_name}</h4>
              {selectedAgent === agent.username && (
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              )}
            </div>
            
            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Total:</span>
                <span className="font-medium">{agent.appointment_count}</span>
              </div>
              <div className="flex justify-between">
                <span>Upcoming:</span>
                <span className="font-medium text-green-600">{agent.upcoming_count}</span>
              </div>
            </div>
            
            <div className="mt-2 text-xs text-blue-600">
              {selectedAgent === agent.username ? 'Click to show all' : 'Click to filter'}
            </div>
          </div>
        ))}
      </div>
      
      {selectedAgent && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              Showing calendar for: {agentStats.find(a => a.username === selectedAgent)?.agent_name}
            </span>
            <button
              onClick={() => setSelectedAgent('')}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear filter
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const calendarDays = generateCalendarDays();

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <CalendarIcon className="h-8 w-8 mr-3" />
              Calendar
            </h1>
            
            {/* Calendar Type Indicator */}
            {isAdmin && (
              <div className="mt-2">
                <span className="text-sm text-gray-600">
                  Viewing: 
                  <span className="font-medium ml-1">
                    {calendarType === 'admin' 
                      ? (selectedAgent 
                          ? `${agentStats.find(a => a.username === selectedAgent)?.agent_name || selectedAgent}'s Calendar`
                          : 'All Agents Calendar'
                        )
                      : 'My Calendar'
                    }
                  </span>
                </span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2 mt-4 sm:mt-0">
            {/* Calendar Type Toggle for Admins */}
            {isAdmin && (
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => {
                    setCalendarType('agent');
                    setSelectedAgent('');
                  }}
                  className={`px-4 py-2 text-sm font-medium ${
                    calendarType === 'agent'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  My Calendar
                </button>
                <button
                  onClick={() => setCalendarType('admin')}
                  className={`px-4 py-2 text-sm font-medium ${
                    calendarType === 'admin'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All Agents
                </button>
              </div>
            )}
            
            {/* Agent Filter Dropdown (Admin Only) */}
            {isAdmin && calendarType === 'admin' && (
              <div className="relative">
                <button
                  onClick={() => setShowAgentDropdown(!showAgentDropdown)}
                  className="flex items-center bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {selectedAgent 
                    ? agentStats.find(a => a.username === selectedAgent)?.agent_name || selectedAgent
                    : 'All Agents'
                  }
                  <ChevronDown className="h-4 w-4 ml-2" />
                </button>
                
                {showAgentDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setSelectedAgent('');
                          setShowAgentDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                          !selectedAgent ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        All Agents ({agentStats.reduce((sum, agent) => sum + agent.appointment_count, 0)} appointments)
                      </button>
                      
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        {agentStats.map(agent => (
                          <button
                            key={agent.agent_id}
                            onClick={() => {
                              setSelectedAgent(agent.username);
                              setShowAgentDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                              selectedAgent === agent.username 
                                ? 'bg-blue-50 text-blue-700 font-medium' 
                                : 'text-gray-700'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span>{agent.agent_name}</span>
                              <span className="text-xs text-gray-500">
                                {agent.appointment_count} appts
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Export Button */}
            <button
              onClick={exportCalendar}
              className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export iCal
            </button>
          </div>
        </div>

        {/* Calendar Subscription - NEW TOKEN-BASED VERSION */}
        {calendarType === 'agent' && subscriptionLoading && <CalendarSubscriptionLoading />}
        {calendarType === 'agent' && !subscriptionLoading && <CalendarSubscriptionPanel />}

        {/* Status Message */}
        {statusMessage.text && (
          <div className={`mb-4 p-3 rounded-lg ${
            statusMessage.type === 'error'
              ? 'bg-red-100 text-red-700 border border-red-200'
              : 'bg-green-100 text-green-700 border border-green-200'
          }`}>
            {statusMessage.text}
          </div>
        )}
      </div>

      {/* Agent Stats (Admin View) */}
      {calendarType === 'admin' && agentStats.length > 0 && <AgentStatsPanel />}

      {/* Calendar Navigation */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            <h2 className="text-xl font-semibold text-gray-900">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          <button
            onClick={goToToday}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600"
          >
            Today
          </button>
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading calendar...</div>
          </div>
        ) : (
          <div className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-0 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-0 border-t border-l border-gray-200">
              {calendarDays.map(renderCalendarDay)}
            </div>
          </div>
        )}
      </div>

      {/* Upcoming Appointments List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Upcoming Appointments
            {selectedAgent && (
              <span className="ml-2 text-sm font-normal text-gray-600">
                - {agentStats.find(a => a.username === selectedAgent)?.agent_name || selectedAgent}
              </span>
            )}
          </h3>
        </div>
        
        <div className="p-4">
          {appointments
            .filter(apt => new Date(apt.date) >= new Date())
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(0, 10)
            .map(appointment => (
              <div key={appointment.id} className="flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">{appointment.title}</h4>
                    <span className="text-sm text-gray-500">
                      {format(parseISO(appointment.date), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  
                  <div className="mt-1 text-sm text-gray-600 space-y-1">
                    {appointment.customer_name && (
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {appointment.customer_name}
                      </div>
                    )}
                    
                    {appointment.location && (
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {appointment.location}
                      </div>
                    )}
                    
                    {calendarType === 'admin' && appointment.agent_name && (
                      <div className="text-blue-600 font-medium">
                        Agent: {appointment.agent_name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          
          {appointments.filter(apt => new Date(apt.date) >= new Date()).length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No upcoming appointments
              {selectedAgent && (
                <div className="text-sm mt-1">
                  for {agentStats.find(a => a.username === selectedAgent)?.agent_name || selectedAgent}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CalendarView;